import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

// Chei pentru localStorage
const OFERTE_PAGINATION_KEY = 'oferte_pagination_state';
const OFERTE_FILTERS_KEY = 'oferte_filters_state';
const OFERTE_CLIENT_PAGINATION_KEY = 'oferte_client_pagination_state';
const OFERTE_CLIENT_FILTERS_KEY = 'oferte_client_filters_state';

// Funcții helper pentru localStorage
const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Eroare la salvarea în localStorage:', error);
  }
};

const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.warn('Eroare la încărcarea din localStorage:', error);
    return defaultValue;
  }
};

export const useOferte = (client_mongo_id) => {
  const { token } = useStateContext();
  const { message } = App.useApp();
  const [oferte, setOferte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState({}); // Pentru loading individual per ofertă
  const [statusOptions, setStatusOptions] = useState([]);
  
  // Determinăm cheile în funcție de context (general sau pentru client specific)
  const paginationKey = client_mongo_id ? OFERTE_CLIENT_PAGINATION_KEY : OFERTE_PAGINATION_KEY;
  const filtersKey = client_mongo_id ? OFERTE_CLIENT_FILTERS_KEY : OFERTE_FILTERS_KEY;
  
  // Încărcăm starea paginației din localStorage
  const [pagination, setPagination] = useState(() => 
    loadFromLocalStorage(paginationKey, {
      current: 1,
      pageSize: 10,
      total: 0
    })
  );
  
  // Încărcăm filtrele din localStorage
  const [filters, setFilters] = useState(() => 
    loadFromLocalStorage(filtersKey, {
      search: '',
      status: 'all',
      manager_id: 'all',
      product_status: 'all',
      sort_by: 'date_added',
      sort_order: 'desc',
      client_name: ''
    })
  );

  const [oferta, setOferta] = useState(null);

  // Salvăm starea paginației în localStorage când se schimbă
  useEffect(() => {
    saveToLocalStorage(paginationKey, pagination);
  }, [pagination, paginationKey]);

  // Salvăm filtrele în localStorage când se schimbă
  useEffect(() => {
    saveToLocalStorage(filtersKey, filters);
  }, [filters, filtersKey]);

  const fetchOferta = useCallback(async (id) => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Procesăm produsele pentru editare
        if (data.info?.new_products) {
          const existingProducts = data.produse.map(p => ({
            id: `existing_${p.id}`,
            existing_product_id: p.id_produs,
            product_name: p.produs?.denumire || 'Produs necunoscut',
            description: p.produs?.descriere_scurta || '',
            quantity: p.product_qt,
            sale_price: p.sale_price,
            is_existing: true,
            source: 'database'
          }));
          
          const newProducts = data.info.new_products.map((p, index) => ({
            ...p,
            id: p.id || `new_${index}`,
            is_existing: false
          }));
          
          data.products = [...existingProducts, ...newProducts];
        }
        
        setOferta(data);
      } else {
        // Gestionăm erorile cu detalii
        let errorMessage = 'Eroare la încărcarea ofertei';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // Dacă nu putem parsa JSON-ul, folosim mesajul generic
        }
        console.error('Eroare response oferta:', response.status, errorMessage);
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Eroare la fetch oferta:', error);  
      message.error('Eroare de conexiune la încărcarea ofertei');
    }
  }, [token]);

        
  

  const fetchStatusOptions = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferta-status?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatusOptions(data);
      }
    } catch (error) {
      console.error('Eroare la încărcarea statusurilor:', error);
    }
  }, [token]);

  const fetchOferte = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Construim parametrii URL
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: 'date_added',
        sort_order: 'desc'
      });

      // Adăugăm filtrele doar dacă au valori
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.manager_id && filters.manager_id !== 'all') {
        params.append('manager_id', filters.manager_id.toString());
      }
      if (filters.product_status && filters.product_status !== 'all') {
        params.append('product_status', filters.product_status.toString());
      }
      if (filters.client_name && filters.client_name.trim()) {
        params.append('client_name', filters.client_name.trim());
      }
      if (filters.client_mongo_id && filters.client_mongo_id !== 'all') {
        params.append('client_mongo_id', filters.client_mongo_id.toString());
      }
      if (filters.oferta_id && filters.oferta_id !== 'all') {
        params.append('oferta_id', filters.oferta_id.toString());
      }
      if (client_mongo_id) {
        params.append('client_mongo_id', client_mongo_id.toString());
      }

      console.log(params.toString())

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/oferte?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOferte(data.items || []);
        setPagination(prev => ({
          ...prev,
          current: data.page || page,
          total: data.total || 0,
          pageSize: data.page_size || pageSize
        }));
      } else {
        // Gestionăm erorile cu detalii
        let errorMessage = 'Eroare la încărcarea ofertelor';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // Dacă nu putem parsa JSON-ul, folosim mesajul generic
        }
        console.error('Eroare response oferte:', response.status, errorMessage);
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Eroare la fetch oferte:', error);
      message.error('Eroare de conexiune la încărcarea ofertelor');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Funcție pentru aplicarea filtrelor
  const applyFilters = useCallback((newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    // Resetăm pagina la 1 când aplicăm filtre noi
    const updatedPagination = { ...pagination, current: 1 };
    setPagination(updatedPagination);
    fetchOferte(1, pagination.pageSize, updatedFilters);
  }, [fetchOferte, pagination.pageSize, filters]);

  // Funcție pentru resetarea filtrelor
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      status: 'all',
      manager_id: 'all',
      product_status: 'all',
      client_name: ''
    };
    setFilters(emptyFilters);
    // Resetăm și paginația când ștergem filtrele
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);
    fetchOferte(1, pagination.pageSize, emptyFilters);
  }, [fetchOferte, pagination.pageSize]);

  const createOferta = useCallback(async (ofertaData) => {

    console.log(ofertaData)
    if (!token) return false;
    
    try {
      // Separăm datele pentru ofertă și produse
      const { existing_products, new_products, ...ofertaPayload } = ofertaData;
      
      // Pregătim payload-ul pentru ofertă
      const payload = {
        ...ofertaPayload,
        info: {
          new_products: new_products || [],
          notes: ofertaData.notes,
          metadata: {
            created_from: 'form',
            has_pending_products: (new_products?.length || 0) > 0
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log(response)

      if (response.ok) {
        const createdOferta = await response.json();
        
        // Dacă avem produse existente, le adăugăm la ofertă
        if (existing_products && existing_products.length > 0) {
          for (const product of existing_products) {
            try {
              await fetch(`${process.env.REACT_APP_API_URL}/oferte/${createdOferta.id}/produse?token=${token}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  id_produs: product.existing_product_id,
                  sale_price: product.sale_price || product.estimated_price || 0,
                  product_qt: product.quantity || 1,
                  bought_price: null
                })
              });
            } catch (error) {
              console.error('Eroare la adăugarea produsului existent:', error);
              // Nu blochez procesul pentru erorile de produse individuale
            }
          }
        }
        
        message.success('Oferta a fost creată cu succes');
        fetchOferte(pagination.current, pagination.pageSize, filters);
        return true;
      } else {
        // Gestionăm erorile cu detalii
        let errorMessage = 'Eroare la crearea ofertei';
        try {
          const errorData = await response.json();
          console.log("Error data:", errorData)
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // Dacă nu putem parsa JSON-ul, folosim mesajul generic
        }
        console.error('Eroare response create oferta:', response.status, errorMessage);
        message.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Eroare la crearea ofertei:', error);
      message.error('Eroare de conexiune la crearea ofertei');
      return false;
    }
  }, [token, fetchOferte, pagination, filters]);

  const updateOferta = useCallback(async (id, ofertaData) => {
    if (!token) return false;

    try {
      // Obținem informațiile existente ale ofertei specifice
      let currentInfo = {};
      try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (fetchResponse.ok) {
          const currentOferta = await fetchResponse.json();
          currentInfo = currentOferta.info || {};
        }
      } catch (fetchError) {
        console.error('Eroare la obținerea informațiilor existente:', fetchError);
        // Continuăm cu info gol dacă nu putem obține datele existente
      }

      // Separăm datele pentru ofertă și produse
      const { existing_products, new_products, ...ofertaPayload } = ofertaData;
      
      // Pregătim payload-ul pentru actualizare, păstrând informațiile existente
      const payload = {
        ...ofertaPayload,
        info: {
          ...currentInfo, // Păstrăm toate informațiile existente
          new_products: new_products || currentInfo.new_products || [],
          notes: ofertaData.notes !== undefined ? ofertaData.notes : currentInfo.notes,
          metadata: {
            ...currentInfo.metadata, // Păstrăm metadata existentă
            updated_from: 'form',
            has_pending_products: (new_products?.length || 0) > 0,
            last_updated: new Date().toISOString()
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Oferta a fost actualizată cu succes');
        fetchOferte(pagination.current, pagination.pageSize, filters);
        return true;
      } else {
        // Gestionăm erorile cu detalii
        let errorMessage = 'Eroare la actualizarea ofertei';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // Dacă nu putem parsa JSON-ul, folosim mesajul generic
        }
        console.error('Eroare response update oferta:', response.status, errorMessage);
        message.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea ofertei:', error);
      message.error('Eroare de conexiune la actualizarea ofertei');
      return false;
    }
  }, [token, fetchOferte, pagination, filters]);

  const deleteOferta = useCallback(async (id) => {
    if (!token) {
      console.log('No token available'); // Debug
      return false;
    }

    console.log('Attempting to delete oferta with id:', id); // Debug
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Delete response status:', response.status); // Debug

      if (response.ok) {
        message.success('Oferta a fost ștearsă cu succes');
        fetchOferte(pagination.current, pagination.pageSize, filters);
        return true;
      } else {
        // Verificăm dacă răspunsul conține JSON
        let errorMessage = 'Eroare la ștergerea ofertei';
        
        try {
          const errorData = await response.json();
          console.log('Delete error data:', errorData); // Debug
          
          // Folosim mesajul de la server dacă există
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (jsonError) {
          // Dacă nu putem parsa JSON-ul, încercăm să obținem textul răspunsului
          console.log('Nu s-a putut parsa JSON-ul de eroare:', jsonError);
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            console.log('Nu s-a putut obține textul de eroare:', textError);
            // Folosim mesajul bazat pe status code
            switch (response.status) {
              case 400:
                errorMessage = 'Cerere invalidă - verifică datele introduse';
                break;
              case 403:
                errorMessage = 'Nu ai permisiunea să ștergi această ofertă';
                break;
              case 404:
                errorMessage = 'Oferta nu a fost găsită';
                break;
              case 409:
                errorMessage = 'Conflictul: Oferta nu poate fi ștearsă în starea actuală';
                break;
              default:
                errorMessage = `Eroare server (${response.status})`;
            }
          }
        }
        
        message.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Eroare la ștergerea ofertei:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token, fetchOferte, pagination, filters]);

  const removePendingProduct = useCallback(async (ofertaId, pendingProductId) => {
    if (!token) return false;

    try {
      // Folosim oferta din state
      const currentOferta = oferta || {};
      
      // Creăm structura info dacă nu există
      const currentInfo = currentOferta.info || {};
      const currentNewProducts = currentInfo.new_products || [];
      
      // Filtrăm produsul de eliminat
      const updatedNewProducts = currentNewProducts.filter(
        product => product.id !== pendingProductId
      );
      
      // Pregătim payload-ul pentru actualizare
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          new_products: updatedNewProducts,
          metadata: {
            ...currentInfo.metadata,
            has_pending_products: updatedNewProducts.length > 0,
            last_updated: new Date().toISOString(),
            manual_products_removed: (currentInfo.metadata?.manual_products_removed || 0) + 1
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Produsul a fost eliminat din lista de așteptare');
        // Actualizăm oferta în state
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la eliminarea produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la eliminarea produsului pending:', error);
      message.error('Eroare la eliminarea produsului');
      return false;
    }
  }, [token, oferta]);

  const addPendingProduct = useCallback(async (ofertaId, pendingProductData) => {
    if (!token) return false;

    try {
      // Folosim oferta din state
      const currentOferta = oferta || {};
      
      // Creăm structura info dacă nu există
      const currentInfo = currentOferta.info || {};
      const currentNewProducts = currentInfo.new_products || [];
      
      // Creăm ID unic pentru produsul pending
      const newProductId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Creăm noul produs pending
      const newPendingProduct = {
        id: newProductId,
        product_name: pendingProductData.product_name,
        description: pendingProductData.description,
        quantity: pendingProductData.quantity,
        estimated_price: pendingProductData.estimated_price || null,
        notes: pendingProductData.notes || 'Adăugat manual de către manager',
        source: 'manual',
        is_existing: false,
        sale_price: pendingProductData.estimated_price || 0,
        specifications: null,
        existing_product_id: null
      };
      
      // Actualizăm lista de produse pending
      const updatedNewProducts = [...currentNewProducts, newPendingProduct];
      
      // Pregătim payload-ul pentru actualizare
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          new_products: updatedNewProducts,
          metadata: {
            ...currentInfo.metadata,
            has_pending_products: true,
            last_updated: new Date().toISOString(),
            manual_products_added: (currentInfo.metadata?.manual_products_added || 0) + 1
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Produsul a fost adăugat în lista de așteptare');
        // Actualizăm oferta în state
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la adăugarea produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la adăugarea produsului pending:', error);
      message.error('Eroare la adăugarea produsului');
      return false;
    }
  }, [token, oferta]);

  const updatePendingProduct = useCallback(async (ofertaId, pendingProductId, updatedData) => {
    if (!token) return false;

    try {
      // Folosim oferta din state
      const currentOferta = oferta || {};
      
      // Creăm structura info dacă nu există
      const currentInfo = currentOferta.info || {};
      const currentNewProducts = currentInfo.new_products || [];
      
      // Actualizăm produsul specific
      const updatedNewProducts = currentNewProducts.map(product => 
        product.id === pendingProductId 
          ? { ...product, ...updatedData }
          : product
      );
      
      // Pregătim payload-ul pentru actualizare
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          new_products: updatedNewProducts,
          metadata: {
            ...currentInfo.metadata,
            last_updated: new Date().toISOString(),
            manual_products_edited: (currentInfo.metadata?.manual_products_edited || 0) + 1
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Produsul a fost actualizat cu succes');
        // Actualizăm oferta în state
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la actualizarea produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea produsului pending:', error);
      message.error('Eroare la actualizarea produsului');
      return false;
    }
  }, [token, oferta]);

  const addComment = useCallback(async (ofertaId, commentData, userInfo) => {
    if (!token) return false;

    try {
      // Folosim oferta din state
      const currentOferta = oferta || {};
      
      // Creăm structura info dacă nu există
      const currentInfo = currentOferta.info || {};
      const currentComments = currentInfo.comments || [];
      
      // Creăm ID unic pentru comentariu
      const commentId = `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Creăm noul comentariu
      const newComment = {
        id: commentId,
        user_id: userInfo.id,
        user_name: userInfo.firstname,
        user_surname: userInfo.lastname,
        comment: commentData.comment,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Actualizăm lista de comentarii
      const updatedComments = [...currentComments, newComment];
      
      // Pregătim payload-ul pentru actualizare
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          comments: updatedComments,
          metadata: {
            ...currentInfo.metadata,
            has_comments: true,
            last_updated: new Date().toISOString(),
            comments_count: updatedComments.length
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Comentariul a fost adăugat cu succes');
        // Actualizăm oferta în state
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la adăugarea comentariului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la adăugarea comentariului:', error);
      message.error('Eroare la adăugarea comentariului');
      return false;
    }
  }, [token, oferta]);

  const removeComment = useCallback(async (ofertaId, commentId) => {
    if (!token) return false;

    try {
      // Folosim oferta din state
      const currentOferta = oferta || {};
      
      // Creăm structura info dacă nu există
      const currentInfo = currentOferta.info || {};
      const currentComments = currentInfo.comments || [];
      
      // Filtrăm comentariul de eliminat
      const updatedComments = currentComments.filter(
        comment => comment.id !== commentId
      );
      
      // Pregătim payload-ul pentru actualizare
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          comments: updatedComments,
          metadata: {
            ...currentInfo.metadata,
            has_comments: updatedComments.length > 0,
            last_updated: new Date().toISOString(),
            comments_count: updatedComments.length
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Comentariul a fost șters cu succes');
        // Actualizăm oferta în state
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la ștergerea comentariului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la ștergerea comentariului:', error);
      message.error('Eroare la ștergerea comentariului');
      return false;
    }
  }, [token, oferta]);

  // Funcție optimizată pentru actualizarea statusului unei oferte
  const updateOfertaStatus = useCallback(async (ofertaId, newStatus) => {
    if (!token) {
      message.error('Nu există token de autentificare');
      return false;
    }

    // Validăm că statusul există în opțiuni
    const statusExists = statusOptions.find(option => option.id === newStatus);
    if (!statusExists) {
      message.error('Status invalid selectat');
      return false;
    }

    // Setăm loading pentru această ofertă specifică
    setStatusLoading(prev => ({ ...prev, [ofertaId]: true }));

    try {
      // Update optimistic în UI
      setOferte(prev => 
        prev.map(oferta => 
          oferta.id === ofertaId 
            ? { ...oferta, status: newStatus }
            : oferta
        )
      );

      // Actualizăm și oferta curentă dacă e aceeași
      if (oferta && oferta.id === ofertaId) {
        setOferta(prev => ({ ...prev, status: newStatus }));
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const statusLabel = statusExists.label;
        message.success(`Statusul ofertei a fost schimbat în "${statusLabel}"`);
        
        // Refresh lista pentru a fi siguri că avem datele actualizate
        fetchOferte(pagination.current, pagination.pageSize, filters);
        
        // Dacă e oferta curentă, o reîncărcăm
        if (oferta && oferta.id === ofertaId) {
          fetchOferta(ofertaId);
        }
        
        return true;
      } else {
        // Rollback optimistic update în caz de eroare
        setOferte(prev => 
          prev.map(oferta => 
            oferta.id === ofertaId 
              ? { ...oferta, status: oferta.status } // revert la statusul original
              : oferta
          )
        );

        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la actualizarea statusului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error);
      
      // Rollback optimistic update
      setOferte(prev => 
        prev.map(oferta => 
          oferta.id === ofertaId 
            ? { ...oferta, status: oferta.status }
            : oferta
        )
      );

      message.error('Eroare de conexiune la actualizarea statusului');
      return false;
    } finally {
      // Scoatem loading pentru această ofertă
      setStatusLoading(prev => {
        const newState = { ...prev };
        delete newState[ofertaId];
        return newState;
      });
    }
  }, [token, statusOptions, oferta, fetchOferte, fetchOferta, pagination, filters]);

  // Funcție pentru a verifica dacă o ofertă are loading pe status
  const isStatusLoading = useCallback((ofertaId) => {
    return statusLoading[ofertaId] || false;
  }, [statusLoading]);


  useEffect(() => {
    // Folosim starea salvată pentru prima încărcare
    if (token) {
      fetchOferte(pagination.current, pagination.pageSize, filters);
      fetchStatusOptions();
    }
  }, [token]); // Rulează când token-ul devine disponibil

  // Adăugăm o funcție helper pentru actualizarea paginației
  const updatePagination = useCallback((newPagination) => {
    setPagination(newPagination);
    fetchOferte(newPagination.current, newPagination.pageSize, filters);
  }, [fetchOferte, filters]);

  // Funcție pentru ștergerea stării salvate (utilă pentru logout sau reset complet)
  const clearSavedState = useCallback(() => {
    try {
      localStorage.removeItem(paginationKey);
      localStorage.removeItem(filtersKey);
      
      // Resetăm starea la valorile default
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0
      });
      
      setFilters({
        search: '',
        status: 'all',
        manager_id: 'all',
        product_status: 'all',
        sort_by: 'date_added',
        sort_order: 'desc',
        client_name: ''
      });
      
    } catch (error) {
      console.warn('Eroare la ștergerea stării salvate:', error);
    }
  }, [paginationKey, filtersKey]);

  return {
    oferte,
    loading,
    pagination,
    statusOptions,
    oferta,
    filters,
    statusLoading,
    fetchOferte,
    fetchOferta,
    createOferta,
    updateOferta,
    updateOfertaStatus,
    isStatusLoading,
    deleteOferta,
    removePendingProduct,
    addPendingProduct,
    updatePendingProduct,
    addComment,
    removeComment,
    applyFilters,
    clearFilters,
    updatePagination,
    clearSavedState
  };
};

// Hook optimizat pentru pagina unei singure oferte (nu face fetch la toate ofertele)
export const useOfertaSingle = () => {
  const { token } = useStateContext();
  const { message } = App.useApp();
  const [oferta, setOferta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusLoading, setStatusLoading] = useState({});

  // Fetch doar pentru statusurile ofertei
  const fetchStatusOptions = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferta-status?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatusOptions(data);
      }
    } catch (error) {
      console.error('Eroare la încărcarea statusurilor:', error);
    }
  }, [token]);

  // Fetch pentru o singură ofertă
  const fetchOferta = useCallback(async (id) => {
    if (!token || !id) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOferta(data);
      } else {
        message.error('Eroare la încărcarea ofertei');
      }
    } catch (error) {
      console.error('Eroare la încărcarea ofertei:', error);
      message.error('Eroare de conexiune');
    } finally {
      setLoading(false);
    }
  }, [token, message]);

  // Actualizarea statusului unei oferte (fără fetch la toate ofertele)
  const updateOfertaStatus = useCallback(async (ofertaId, newStatus) => {
    if (!token) {
      message.error('Nu există token de autentificare');
      return false;
    }

    const statusExists = statusOptions.find(option => option.id === newStatus);
    if (!statusExists) {
      message.error('Status invalid selectat');
      return false;
    }

    setStatusLoading(prev => ({ ...prev, [ofertaId]: true }));

    try {
      // Update optimistic în UI pentru oferta curentă
      if (oferta && oferta.id === ofertaId) {
        setOferta(prev => ({ ...prev, status: newStatus }));
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const statusLabel = statusExists.label;
        message.success(`Statusul ofertei a fost schimbat în "${statusLabel}"`);
        
        // Reîncarcă doar oferta curentă
        if (oferta && oferta.id === ofertaId) {
          fetchOferta(ofertaId);
        }
        
        return true;
      } else {
        // Rollback optimistic update în caz de eroare
        if (oferta && oferta.id === ofertaId) {
          setOferta(prev => ({ ...prev, status: oferta.status }));
        }

        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la actualizarea statusului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error);
      
      // Rollback optimistic update
      if (oferta && oferta.id === ofertaId) {
        setOferta(prev => ({ ...prev, status: oferta.status }));
      }

      message.error('Eroare de conexiune la actualizarea statusului');
      return false;
    } finally {
      setStatusLoading(prev => {
        const newState = { ...prev };
        delete newState[ofertaId];
        return newState;
      });
    }
  }, [token, statusOptions, oferta, fetchOferta, message]);

  // Funcțiile pentru produse pending și comentarii (copiate din hook-ul original)
  const removePendingProduct = useCallback(async (ofertaId, pendingProductId) => {
    if (!token) return false;

    try {
      const currentOferta = oferta || {};
      const currentInfo = currentOferta.info || {};
      const currentNewProducts = currentInfo.new_products || [];
      
      const updatedNewProducts = currentNewProducts.filter(
        product => product.id !== pendingProductId
      );
      
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          new_products: updatedNewProducts,
          metadata: {
            ...currentInfo.metadata,
            has_pending_products: updatedNewProducts.length > 0,
            last_updated: new Date().toISOString(),
            pending_products_count: updatedNewProducts.length
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Produsul a fost eliminat cu succes');
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la eliminarea produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la eliminarea produsului pending:', error);
      message.error('Eroare la eliminarea produsului');
      return false;
    }
  }, [token, oferta, message]);

  const addPendingProduct = useCallback(async (ofertaId, productData) => {
    if (!token) return false;

    try {
      const currentOferta = oferta || {};
      const currentInfo = currentOferta.info || {};
      const currentNewProducts = currentInfo.new_products || [];
      
      const newProduct = {
        id: Date.now(),
        product_name: productData.product_name,
        quantity: productData.quantity || 1,
        description: productData.description || '',
        created_at: new Date().toISOString()
      };
      
      const updatedNewProducts = [...currentNewProducts, newProduct];
      
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          new_products: updatedNewProducts,
          metadata: {
            ...currentInfo.metadata,
            has_pending_products: true,
            last_updated: new Date().toISOString(),
            pending_products_count: updatedNewProducts.length,
            manual_products_added: (currentInfo.metadata?.manual_products_added || 0) + 1
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Produsul a fost adăugat cu succes');
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la adăugarea produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la adăugarea produsului pending:', error);
      message.error('Eroare la adăugarea produsului');
      return false;
    }
  }, [token, oferta, message]);

  const addComment = useCallback(async (ofertaId, commentData, userInfo) => {
    if (!token) return false;

    try {
      const currentOferta = oferta || {};
      const currentInfo = currentOferta.info || {};
      const currentComments = currentInfo.comments || [];
      
      const newComment = {
        id: Date.now(),
        content: commentData.content,
        author: userInfo?.name || 'Utilizator necunoscut',
        author_id: userInfo?.id || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const updatedComments = [...currentComments, newComment];
      
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          comments: updatedComments,
          metadata: {
            ...currentInfo.metadata,
            has_comments: true,
            last_updated: new Date().toISOString(),
            comments_count: updatedComments.length
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Comentariul a fost adăugat cu succes');
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la adăugarea comentariului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la adăugarea comentariului:', error);
      message.error('Eroare la adăugarea comentariului');
      return false;
    }
  }, [token, oferta, message]);

  const removeComment = useCallback(async (ofertaId, commentId) => {
    if (!token) return false;

    try {
      const currentOferta = oferta || {};
      const currentInfo = currentOferta.info || {};
      const currentComments = currentInfo.comments || [];
      
      const updatedComments = currentComments.filter(
        comment => comment.id !== commentId
      );
      
      const payload = {
        ...currentOferta,
        info: {
          ...currentInfo,
          comments: updatedComments,
          metadata: {
            ...currentInfo.metadata,
            has_comments: updatedComments.length > 0,
            last_updated: new Date().toISOString(),
            comments_count: updatedComments.length
          }
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Comentariul a fost șters cu succes');
        setOferta(payload);
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la ștergerea comentariului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la ștergerea comentariului:', error);
      message.error('Eroare la ștergerea comentariului');
      return false;
    }
  }, [token, oferta, message]);

  const isStatusLoading = useCallback((ofertaId) => {
    return statusLoading[ofertaId] || false;
  }, [statusLoading]);

  // Inițializează doar statusurile la mount (fără fetch la toate ofertele)
  useEffect(() => {
    fetchStatusOptions();
  }, [fetchStatusOptions]);

  return {
    oferta,
    loading,
    statusOptions,
    statusLoading,
    fetchOferta,
    updateOfertaStatus,
    isStatusLoading,
    removePendingProduct,
    addPendingProduct,
    addComment,
    removeComment
  };
}; 